<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useSuppliersStore } from 'dashboard/stores/suppliers';
import SupplierAPI from 'dashboard/api/suppliers';
import SupplierCertificatesAPI from 'dashboard/api/supplierCertificates';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SupplierFormModal from 'dashboard/components-next/Suppliers/SupplierFormModal.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const suppliersStore = useSuppliersStore();

const supplierFormModalRef = ref(null);
const certificateDialogRef = ref(null);
const isUpdating = ref(false);
const isSavingCertificate = ref(false);
const editingCertificate = ref(null);
const selectedFile = ref(null);
const isDraggingCertificateFile = ref(false);
let dragDepth = 0;

const emptyCertificateForm = () => ({
  name: '',
  issued_on: '',
  expires_on: '',
});

const certificateForm = reactive(emptyCertificateForm());

const supplier = computed(() => {
  const supplierId = route.params.supplierId;
  return suppliersStore.getSupplier(supplierId);
});

const certificates = computed(() => supplier.value?.certificates || []);

const certificateDialogTitle = computed(() =>
  editingCertificate.value
    ? t('SUPPLIERS.DETAILS.EDIT_CERTIFICATE')
    : t('SUPPLIERS.DETAILS.ADD_CERTIFICATE')
);

const isCertificateFormValid = computed(
  () => certificateForm.name.trim().length > 0
);

const certificateFileMessage = computed(() => {
  if (selectedFile.value) return selectedFile.value.name;
  const existingName =
    editingCertificate.value?.fileFilename ||
    editingCertificate.value?.file_filename;
  if (existingName) {
    return t('SUPPLIERS.DETAILS.CURRENT_FILE', { name: existingName });
  }
  return '';
});

const goBack = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/suppliers?page=1`);
  }
};

const openEditModal = () => {
  supplierFormModalRef.value?.open();
};

const syncUsers = async (supplierId, userIds) => {
  const { data } = await SupplierAPI.getUsers(supplierId);
  const currentIds = (data.payload || []).map(user => user.id);
  const desiredIds = (userIds || []).map(Number);
  const toAdd = desiredIds.filter(id => !currentIds.includes(id));
  const toRemove = currentIds.filter(id => !desiredIds.includes(id));
  await Promise.all([
    ...toAdd.map(id => SupplierAPI.addUser(supplierId, id)),
    ...toRemove.map(id => SupplierAPI.removeUser(supplierId, id)),
  ]);
};

const handleUpdateSupplier = async payload => {
  isUpdating.value = true;
  try {
    const { user_ids: userIds, ...supplierPayload } = payload;
    await suppliersStore.update({
      ...supplierPayload,
      id: supplier.value.id,
    });
    await syncUsers(supplier.value.id, userIds);
    useAlert(t('SUPPLIERS.FORM.UPDATE_SUCCESS'));
    supplierFormModalRef.value?.close();
    await suppliersStore.show(route.params.supplierId);
  } catch (error) {
    const message = error.message || t('SUPPLIERS.FORM.UPDATE_ERROR');
    useAlert(message);
  } finally {
    isUpdating.value = false;
  }
};

const refreshSupplier = async () => {
  await suppliersStore.show(route.params.supplierId);
};

const resetCertificateForm = () => {
  Object.assign(certificateForm, emptyCertificateForm());
  editingCertificate.value = null;
  selectedFile.value = null;
  isDraggingCertificateFile.value = false;
  dragDepth = 0;
};

const applyCertificateFile = file => {
  if (!file) return;
  selectedFile.value = file;
  if (!certificateForm.name.trim()) {
    certificateForm.name = file.name.replace(/\.[^.]+$/, '');
  }
};

const openAddCertificate = () => {
  resetCertificateForm();
  certificateDialogRef.value?.open();
};

const openEditCertificate = certificate => {
  editingCertificate.value = certificate;
  certificateForm.name = certificate.name || '';
  certificateForm.issued_on = certificate.issuedOn || certificate.issued_on || '';
  certificateForm.expires_on =
    certificate.expiresOn || certificate.expires_on || '';
  selectedFile.value = null;
  certificateDialogRef.value?.open();
};

const onFileChange = event => {
  applyCertificateFile(event.target.files?.[0] || null);
};

const onCertificateDragEnter = event => {
  event.preventDefault();
  dragDepth += 1;
  if (event.dataTransfer?.types?.includes('Files')) {
    isDraggingCertificateFile.value = true;
  }
};

const onCertificateDragOver = event => {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
};

const onCertificateDragLeave = event => {
  event.preventDefault();
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) isDraggingCertificateFile.value = false;
};

const onCertificateDrop = event => {
  event.preventDefault();
  isDraggingCertificateFile.value = false;
  dragDepth = 0;
  applyCertificateFile(event.dataTransfer?.files?.[0] || null);
};

const formatCertificateDates = certificate => {
  const issued = certificate.issuedOn || certificate.issued_on;
  const expires = certificate.expiresOn || certificate.expires_on;
  if (issued && expires) return `${issued} → ${expires}`;
  if (expires) return t('SUPPLIERS.DETAILS.EXPIRES_ON', { date: expires });
  if (issued) return t('SUPPLIERS.DETAILS.ISSUED_ON', { date: issued });
  return t('SUPPLIERS.DETAILS.NO_DATES');
};

const certificateStatusLabel = certificate => {
  if (certificate.expired) return t('SUPPLIERS.DETAILS.STATUS_EXPIRED');
  if (certificate.expiringSoon || certificate.expiring_soon) {
    return t('SUPPLIERS.DETAILS.STATUS_EXPIRING');
  }
  return null;
};

const handleSaveCertificate = async () => {
  if (!isCertificateFormValid.value || !supplier.value) return;

  isSavingCertificate.value = true;
  const payload = {
    name: certificateForm.name.trim(),
    issued_on: certificateForm.issued_on || null,
    expires_on: certificateForm.expires_on || null,
  };
  if (selectedFile.value) payload.file = selectedFile.value;

  try {
    if (editingCertificate.value) {
      await SupplierCertificatesAPI.update(
        supplier.value.id,
        editingCertificate.value.id,
        payload
      );
    } else {
      await SupplierCertificatesAPI.create(supplier.value.id, payload);
    }
    useAlert(t('SUPPLIERS.DETAILS.CERTIFICATE_SAVED'));
    certificateDialogRef.value?.close();
    resetCertificateForm();
    await refreshSupplier();
  } catch (error) {
    useAlert(t('SUPPLIERS.DETAILS.CERTIFICATE_SAVE_ERROR'));
  } finally {
    isSavingCertificate.value = false;
  }
};

const handleDeleteCertificate = async certificate => {
  if (!supplier.value) return;

  try {
    await SupplierCertificatesAPI.delete(supplier.value.id, certificate.id);
    useAlert(t('SUPPLIERS.DETAILS.CERTIFICATE_DELETED'));
    await refreshSupplier();
  } catch (error) {
    useAlert(t('SUPPLIERS.DETAILS.CERTIFICATE_DELETE_ERROR'));
  }
};

onMounted(async () => {
  if (route.params.supplierId) {
    await suppliersStore.show(route.params.supplierId);
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <button
            class="inline-flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-4" />
            {{ t('SUPPLIERS.DETAILS.BACK_TO_LIST') }}
          </button>
          <Button
            v-if="supplier"
            size="sm"
            icon="i-lucide-pencil"
            :label="t('SUPPLIERS.DETAILS.EDIT')"
            @click="openEditModal"
          />
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto py-4">
        <div v-if="supplier" class="flex flex-col gap-6">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ supplier.name || t('SUPPLIERS.UNNAMED') }}
          </h1>

          <div class="flex flex-col gap-4 pt-6 border-t border-n-strong">
            <h2 class="text-lg font-medium text-n-slate-12">
              {{ t('SUPPLIERS.DETAILS.INFORMATION') }}
            </h2>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('SUPPLIERS.FORM.FIELDS.OWNER') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ supplier.owner?.name || t('SUPPLIERS.DETAILS.NONE') }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('SUPPLIERS.FORM.FIELDS.CONTACT') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ supplier.contact?.name || t('SUPPLIERS.DETAILS.NONE') }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-4 pt-6 border-t border-n-strong">
            <h2 class="text-lg font-medium text-n-slate-12">
              {{ t('SUPPLIERS.FORM.FIELDS.USERS') }}
            </h2>
            <div
              v-if="!(supplier.users || []).length"
              class="text-sm text-n-slate-11 py-2"
            >
              {{ t('SUPPLIERS.DETAILS.USERS_EMPTY') }}
            </div>
            <ul v-else class="flex flex-col gap-2">
              <li
                v-for="user in supplier.users"
                :key="user.id"
                class="text-sm text-n-slate-12"
              >
                {{ user.name }}
                <span v-if="user.email" class="text-n-slate-11">
                  ({{ user.email }})
                </span>
              </li>
            </ul>
          </div>

          <div class="flex flex-col gap-4 pt-6 border-t border-n-strong">
            <div class="flex items-center justify-between gap-2">
              <h2 class="text-lg font-medium text-n-slate-12">
                {{ t('SUPPLIERS.DETAILS.CERTIFICATES') }}
              </h2>
              <Button
                size="sm"
                icon="i-lucide-plus"
                :label="t('SUPPLIERS.DETAILS.ADD_CERTIFICATE')"
                @click="openAddCertificate"
              />
            </div>

            <div
              v-if="!certificates.length"
              class="text-sm text-n-slate-11 py-2"
            >
              {{ t('SUPPLIERS.DETAILS.CERTIFICATES_EMPTY') }}
            </div>

            <ul v-else class="flex flex-col gap-3">
              <li
                v-for="certificate in certificates"
                :key="certificate.id"
                class="flex items-start justify-between gap-4 p-4 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
              >
                <div class="flex flex-col gap-1 min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ certificate.name }}
                    </span>
                    <span
                      v-if="certificateStatusLabel(certificate)"
                      class="rounded-full px-2 py-0.5 text-xs font-medium"
                      :class="
                        certificate.expired
                          ? 'bg-n-ruby-3 text-n-ruby-11'
                          : 'bg-woot-50 text-woot-600 dark:bg-woot-900/20 dark:text-woot-300'
                      "
                    >
                      {{ certificateStatusLabel(certificate) }}
                    </span>
                  </div>
                  <span class="text-xs text-n-slate-11">
                    {{ formatCertificateDates(certificate) }}
                  </span>
                  <a
                    v-if="certificate.fileUrl || certificate.file_url"
                    :href="certificate.fileUrl || certificate.file_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-woot-500 hover:text-woot-600 truncate"
                  >
                    {{
                      certificate.fileFilename ||
                      certificate.file_filename ||
                      t('SUPPLIERS.DETAILS.DOWNLOAD_FILE')
                    }}
                  </a>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="i-lucide-pencil"
                    @click="openEditCertificate(certificate)"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash-2"
                    @click="handleDeleteCertificate(certificate)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div v-else class="flex items-center justify-center py-10">
          <span class="text-n-slate-11">{{ t('SUPPLIERS.LOADING') }}</span>
        </div>
      </div>
    </main>

    <SupplierFormModal
      ref="supplierFormModalRef"
      :supplier="supplier"
      :is-loading="isUpdating"
      @save="handleUpdateSupplier"
    />

    <Dialog
      ref="certificateDialogRef"
      type="edit"
      :title="certificateDialogTitle"
      :confirm-button-label="t('SUPPLIERS.FORM.UPDATE')"
      :cancel-button-label="t('SUPPLIERS.FORM.CANCEL')"
      :disable-confirm-button="!isCertificateFormValid"
      :is-loading="isSavingCertificate"
      width="lg"
      @confirm="handleSaveCertificate"
      @close="resetCertificateForm"
    >
      <form
        class="flex flex-col gap-4 rounded-xl p-1 transition-colors"
        :class="
          isDraggingCertificateFile
            ? 'bg-n-brand/5 outline outline-2 outline-dashed outline-n-brand'
            : ''
        "
        @submit.prevent="handleSaveCertificate"
        @dragenter="onCertificateDragEnter"
        @dragover="onCertificateDragOver"
        @dragleave="onCertificateDragLeave"
        @drop="onCertificateDrop"
      >
        <Input
          v-model="certificateForm.name"
          :label="t('SUPPLIERS.DETAILS.CERTIFICATE_NAME')"
          :placeholder="t('SUPPLIERS.DETAILS.CERTIFICATE_NAME_PLACEHOLDER')"
          required
        />
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="certificateForm.issued_on"
            type="date"
            :label="t('SUPPLIERS.DETAILS.ISSUED_ON_LABEL')"
          />
          <Input
            v-model="certificateForm.expires_on"
            type="date"
            :label="t('SUPPLIERS.DETAILS.EXPIRES_ON_LABEL')"
          />
        </div>
        <Input
          type="file"
          :label="t('SUPPLIERS.DETAILS.CERTIFICATE_FILE')"
          :message="
            isDraggingCertificateFile
              ? t('SUPPLIERS.DETAILS.DROP_FILE')
              : certificateFileMessage || t('SUPPLIERS.DETAILS.DROP_FILE_HINT')
          "
          @input="onFileChange"
        />
      </form>
    </Dialog>
  </div>
</template>
